(function () {
  const STORAGE_KEY = 'infScroll_' + location.pathname;
  const RESTORE_TTL_MS = 10 * 60 * 1000;

  let grid = null;
  let pagination = null;
  let sentinelObserver = null;
  let sentinel = null;
  let isFetching = false;
  let isRestoring = false;
  let loadedPage = 1;
  let initialPage = 1;
  let scrollRaf = null;
  let lastUrlPage = null;
  let gridReplacementObserver = null;
  let scrollListenerAttached = false;
  let resizeListenerAttached = false;
  let resizeDebounce = null;
  // Cache of marker positions so updateUrlFromScroll doesn't force a layout
  // read on every scroll frame. Rebuilt on append/prepend/resize/init.
  let markerCache = [];

  function cleanSearch(s) {
    const params = new URLSearchParams(s);
    params.delete('page');
    return params.toString();
  }

  function cleanup() {
    if (sentinelObserver) sentinelObserver.disconnect();
    sentinelObserver = null;
    if (sentinel) sentinel.remove();
    sentinel = null;
  }

  function rebuildMarkerCache() {
    if (!grid) { markerCache = []; return; }
    const markers = grid.querySelectorAll('[data-inf-page]');
    const scrollY = window.scrollY;
    markerCache = Array.from(markers, (el) => ({
      pageNum: el.dataset.infPage,
      offsetTop: el.getBoundingClientRect().top + scrollY,
    }));
  }

  function init() {
    cleanup();
    grid = document.querySelector('#product-grid, [id^="product-grid"]');
    pagination = document.querySelector('.pagination');
    if (!grid) return;

    initialPage = parseInt(new URL(location.href).searchParams.get('page') || '1', 10);
    loadedPage = initialPage;
    lastUrlPage = String(initialPage);

    const first = grid.firstElementChild;
    if (first) first.dataset.infPage = String(loadedPage);

    setupSentinelObserver();
    rebuildMarkerCache();

    if (!scrollListenerAttached) {
      window.addEventListener('scroll', onScroll, { passive: true });
      scrollListenerAttached = true;
    }
    if (!resizeListenerAttached) {
      window.addEventListener('resize', () => {
        clearTimeout(resizeDebounce);
        resizeDebounce = setTimeout(rebuildMarkerCache, 150);
      }, { passive: true });
      resizeListenerAttached = true;
    }
    if (!gridReplacementObserver) setupGridReplacementObserver();

    tryRestore();
  }

  function nextPageUrl() {
    if (!pagination) return null;
    const relNext = pagination.querySelector('a[rel="next"]');
    if (relNext) return relNext.href;
    const target = loadedPage + 1;
    const links = Array.from(pagination.querySelectorAll('a[href]'));
    const next = links.find((a) => {
      const p = new URL(a.href, location.origin).searchParams.get('page');
      return parseInt(p, 10) === target;
    });
    return next ? next.href : null;
  }

  function showLoader() {
    if (isRestoring) return;
    let loader = document.getElementById('inf-scroll-loader');
    if (!loader) {
      loader = document.createElement('div');
      loader.id = 'inf-scroll-loader';
      loader.style.cssText = 'text-align:center;padding:2rem;opacity:0.6;font-size:0.9rem;display:block !important;';
      loader.textContent = 'Laster produkter…';
      grid.after(loader);
    }
    loader.style.display = 'block';
  }

  function hideLoader() {
    const loader = document.getElementById('inf-scroll-loader');
    if (loader) loader.style.display = 'none';
  }

  function parsePageHtml(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newGrid = doc.querySelector('#product-grid, [id^="product-grid"]');
    return {
      products: newGrid ? Array.from(newGrid.children) : [],
      pagination: doc.querySelector('.pagination'),
    };
  }

  function appendPage(html, pageNum) {
    const { products, pagination: newPagination } = parsePageHtml(html);
    if (products[0]) products[0].dataset.infPage = String(pageNum);
    products.forEach((child) => grid.appendChild(child));
    return newPagination;
  }

  function prependPage(html, pageNum, beforeNode) {
    const { products } = parsePageHtml(html);
    if (products[0]) products[0].dataset.infPage = String(pageNum);
    products.forEach((child) => grid.insertBefore(child, beforeNode));
  }

  function applyNewPagination(newPagination) {
    if (newPagination) {
      if (pagination) pagination.replaceWith(newPagination);
      pagination = newPagination;
      setupSentinelObserver();
    } else {
      if (pagination) pagination.remove();
      pagination = null;
      cleanup();
    }
  }

  function pageUrl(p) {
    const url = new URL(location.href);
    if (p === 1) url.searchParams.delete('page');
    else url.searchParams.set('page', p);
    return url.toString();
  }

  async function fetchPages(pages) {
    const promises = pages.map((p) =>
      fetch(pageUrl(p))
        .then((r) => r.text())
        .then((html) => ({ page: p, html }))
        .catch(() => null)
    );
    return (await Promise.all(promises)).filter(Boolean).sort((a, b) => a.page - b.page);
  }

  async function loadNextPage() {
    if (isFetching || isRestoring) return false;
    const url = nextPageUrl();
    if (!url) return false;

    isFetching = true;
    showLoader();
    try {
      const html = await fetch(url).then((r) => r.text());
      // Restoration may have started during our fetch — bail before mutating state
      if (isRestoring) return false;
      // Filter change (or any other replacement) detached the grid mid-fetch;
      // appending now would write into an orphaned node. Drop the result.
      if (!document.contains(grid)) return false;
      loadedPage++;
      const newPagination = appendPage(html, loadedPage);
      applyNewPagination(newPagination);
      rebuildMarkerCache();
      return true;
    } catch (e) {
      console.error('[Infinite scroll] failed:', e);
      return false;
    } finally {
      hideLoader();
      isFetching = false;
    }
  }

  function setupSentinelObserver() {
    if (sentinelObserver) sentinelObserver.disconnect();
    if (sentinel) sentinel.remove();
    sentinel = null;
    if (!pagination || !nextPageUrl()) return;

    sentinel = document.createElement('div');
    sentinel.id = 'inf-scroll-sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'height:1px;display:block !important;';
    grid.after(sentinel);

    sentinelObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadNextPage();
      },
      { rootMargin: '800px 0px' }
    );
    sentinelObserver.observe(sentinel);
  }

  function updateUrlFromScroll() {
    if (!markerCache.length) return;

    // 100px = "scrolled past the top of viewport" threshold (matches the old behavior).
    const threshold = window.scrollY + 100;
    let pageNum = markerCache[0].pageNum;
    for (const m of markerCache) {
      if (m.offsetTop <= threshold) pageNum = m.pageNum;
      else break;
    }
    if (!pageNum || pageNum === lastUrlPage) return;
    lastUrlPage = pageNum;

    const url = new URL(location.href);
    if (pageNum === '1') {
      if (url.searchParams.get('page') !== null) {
        url.searchParams.delete('page');
        history.replaceState(null, '', url.toString());
      }
    } else {
      url.searchParams.set('page', pageNum);
      history.replaceState(null, '', url.toString());
    }
  }

  function onScroll() {
    if (scrollRaf) return;
    scrollRaf = requestAnimationFrame(() => {
      scrollRaf = null;
      updateUrlFromScroll();
    });
  }

  function setupGridReplacementObserver() {
    const root = document.querySelector('#ProductGridContainer')
      || document.querySelector('.collection')
      || document.querySelector('main')
      || document.body;

    gridReplacementObserver = new MutationObserver(() => {
      if (grid && !document.contains(grid)) {
        setTimeout(() => {
          // Re-check: if the grid is back in the DOM, this was a transient
          // detachment from a 3rd-party script (badges, etc.) — ignore it.
          if (document.contains(grid)) return;
          // Truly replaced (filter change) → re-init.
          init();
        }, 50);
      }
    });
    gridReplacementObserver.observe(root, { childList: true, subtree: true });
  }

  function findAnchorProduct() {
    if (!grid) return null;
    let bestCard = null;
    let bestScore = Infinity;
    for (const card of grid.children) {
      const top = card.getBoundingClientRect().top;
      if (top > window.innerHeight) break;
      const score = Math.abs(top);
      if (score < bestScore) {
        bestScore = score;
        bestCard = card;
      }
    }
    if (!bestCard) return null;
    const link = bestCard.querySelector('a[href]');
    if (!link) return null;
    return {
      href: link.getAttribute('href'),
      offsetTop: bestCard.getBoundingClientRect().top,
    };
  }

  function scrollToAnchor(anchor) {
    if (!anchor || !grid) return false;
    let matchingLink = null;
    for (const a of grid.querySelectorAll('a[href]')) {
      if (a.getAttribute('href') === anchor.href) {
        matchingLink = a;
        break;
      }
    }
    if (!matchingLink) return false;
    const card = matchingLink.closest('li') || matchingLink.parentElement;
    if (!card) return false;
    const rect = card.getBoundingClientRect();
    window.scrollTo(0, rect.top + window.scrollY - anchor.offsetTop);
    return true;
  }

  async function tryRestore() {
    let raw;
    try { raw = sessionStorage.getItem(STORAGE_KEY); } catch (e) { return; }
    if (!raw) return;
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) {}

    // Only restore on back/forward navigation or page reload.
    // Fresh navigation (clicked a link, typed the URL) should start clean.
    const navEntries = performance.getEntriesByType('navigation');
    const navType = navEntries[0] ? navEntries[0].type : 'navigate';
    if (navType !== 'back_forward' && navType !== 'reload') return;

    try {
      const { pagesLoaded, scrollY, anchor, timestamp, search } = JSON.parse(raw);
      if (Date.now() - timestamp > RESTORE_TTL_MS) return;
      if (search !== cleanSearch(location.search)) return;

      isRestoring = true;

      // Fade grid out so the user doesn't see content shifting / scroll snapping
      if (grid) {
        grid.style.transition = 'opacity 150ms ease-out';
        grid.style.opacity = '0';
      }

      // Forward: append pages above the server-rendered initial page
      if (loadedPage < pagesLoaded) {
        const pagesToFetch = [];
        for (let p = loadedPage + 1; p <= pagesLoaded; p++) pagesToFetch.push(p);

        const results = await fetchPages(pagesToFetch);

        let lastPagination = null;
        for (const { page, html } of results) {
          const { pagination: pg } = parsePageHtml(html);
          appendPage(html, page);
          loadedPage = page;
          lastPagination = pg;
        }
        applyNewPagination(lastPagination);
      }

      // Backward: prepend pages 1 through initialPage-1 (refresh / direct-load case)
      if (initialPage > 1) {
        const pagesToFetch = [];
        for (let p = 1; p < initialPage; p++) pagesToFetch.push(p);

        const results = await fetchPages(pagesToFetch);
        const beforeNode = grid.firstElementChild;

        for (const { page, html } of results) {
          prependPage(html, page, beforeNode);
        }
      }

      isRestoring = false;
      rebuildMarkerCache();

      requestAnimationFrame(() => {
        if (anchor && scrollToAnchor(anchor)) {
          setTimeout(() => { scrollToAnchor(anchor); rebuildMarkerCache(); }, 200);
          setTimeout(() => { scrollToAnchor(anchor); rebuildMarkerCache(); }, 800);
        } else if (scrollY) {
          window.scrollTo(0, scrollY);
        }
        if (grid) grid.style.opacity = '1';
      });
    } catch (e) {
      isRestoring = false;
      if (grid) grid.style.opacity = '1';
      console.warn('[Infinite scroll] restore failed', e);
    }
  }

  window.addEventListener('pagehide', () => {
    if (loadedPage > initialPage || window.scrollY > 200) {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
          pagesLoaded: loadedPage,
          scrollY: window.scrollY,
          anchor: findAnchorProduct(),
          timestamp: Date.now(),
          search: cleanSearch(location.search),
        }));
      } catch (e) {}
    }

    const url = new URL(location.href);
    if (url.searchParams.has('page')) {
      url.searchParams.delete('page');
      history.replaceState(null, '', url.toString());
    }
  });

  init();
})();